/**
 * The live preview's layout (section 7): one screen of the app worked out from the draft catalogue
 * (the preview feed) and the screen's rows as they stand in the editor (saved or not). A hero, then
 * rows of cards in the shapes the apps use: posters (2:3), wide cards (16:9), large editorial
 * cards, squares, numbered posters, a shelf of collections; rows built from each viewer's own data
 * (Continue watching, My list) get a sample and say so.
 *
 * It is a faithful sketch of the apps' layout, not the native apps. Pure (no React, no imports at
 * run time), unit-tested; preview.tsx only draws what this returns.
 */

import type { Rail } from "../screens/rows";

type Localized = Record<string, string>;
interface ImageLike { url: string; color?: string; sizes?: Array<{ width: number; url: string }> }
type Artwork = Partial<Record<"poster" | "backdrop" | "thumbnail" | "square" | "logo" | "hero" | "topShelf", ImageLike>>;

export interface FeedItem {
  id: string;
  title: Localized;
  kind?: string;
  synopsis?: Localized;
  artwork?: Artwork;
  entitlement?: { access?: string };
  releasedAt?: string;
  durationSeconds?: number;
  startsAt?: string;
  endsAt?: string;
  terms?: string[];
  seasons?: Array<{ titleIds: string[]; title?: Localized }>;
  stageId?: string;
}
export interface FeedCollection { id: string; title: Localized; layout: string; itemIds: string[]; artwork?: Artwork }
export interface FeedLike {
  pages: Array<{ id: string; title: Localized; rails: Rail[] }>;
  collections: FeedCollection[];
  titles: FeedItem[];
  series: FeedItem[];
  liveEvents: FeedItem[];
  taxonomies?: Array<{ id: string; title: Localized; terms: Array<{ id: string; title: Localized }> }>;
  stages?: Array<{ id: string; title: Localized }>;
}

export type CardShape = "landscape" | "poster" | "editorial" | "square" | "numbered" | "collection";

export interface PreviewBadge { label: string; tone: "accent" | "neutral" | "live" }

export interface PreviewCard {
  id: string;
  title: string;
  meta?: string;
  image?: string;
  /** The image's dominant colour, drawn while it loads (feed data). */
  color?: string;
  badges: PreviewBadge[];
  progress?: number;
  rank?: number;
}

export interface PreviewRow {
  /** The row's place in the editor's list, so the preview and the list select each other. */
  index: number;
  title: string;
  shape: CardShape;
  display: "row" | "grid" | "banner" | "schedule";
  cards: PreviewCard[];
  /** "A sample: each viewer sees their own." */
  sample?: string;
  /** Why the row shows nothing in the apps, said in the console only. */
  empty?: string;
  schedule?: Array<{ stage: string; blocks: Array<{ title: string; start: number; end: number }> }>;
}

export interface PreviewHero {
  title: string;
  synopsis?: string;
  image?: string;
  logo?: string;
  color?: string;
  meta?: string;
  badges: PreviewBadge[];
  fromRow: number;
}

export interface PreviewScreen {
  hero: PreviewHero | null;
  rows: PreviewRow[];
}

export interface LayoutInput {
  feed: FeedLike;
  /** The rows to draw, with their place in the editor's list (hidden rows left out by the caller). */
  rails: Array<{ rail: Rail; index: number }>;
  /** A row's title when it has none of its own (rows.ts rowTypeOf(kind, pack).label). */
  rowLabel: (kind: string) => string;
  locale: string;
  now: number;
}

const DAY = 86_400_000;

export function text(v: Localized | undefined, locale: string): string {
  return v?.[locale] || Object.values(v ?? {})[0] || "";
}

/** The best image of the first role present, at least `width` px wide when there is one. */
export function imageFor(artwork: Artwork | undefined, roles: Array<keyof Artwork>, width: number): { url?: string; color?: string } {
  for (const role of roles) {
    const img = artwork?.[role];
    if (!img?.url) continue;
    const sizes = [...(img.sizes ?? [])].sort((a, b) => a.width - b.width);
    const fit = sizes.find((s) => s.width >= width) ?? sizes.at(-1);
    return { url: fit?.url ?? img.url, color: img.color };
  }
  return {};
}

const ROLES: Record<CardShape, { roles: Array<keyof Artwork>; width: number }> = {
  landscape: { roles: ["thumbnail", "backdrop", "hero", "poster"], width: 480 },
  poster: { roles: ["poster", "thumbnail", "backdrop"], width: 240 },
  numbered: { roles: ["poster", "thumbnail", "backdrop"], width: 240 },
  editorial: { roles: ["backdrop", "hero", "thumbnail", "poster"], width: 960 },
  square: { roles: ["square", "thumbnail", "poster"], width: 240 },
  collection: { roles: ["backdrop", "hero", "thumbnail", "poster"], width: 640 },
};

/** A collection's layout to the shape of its cards and how the row is laid out. */
export function shapeOf(layout: string | undefined): { shape: CardShape; display: PreviewRow["display"] } {
  switch (layout) {
    case "poster-row": return { shape: "poster", display: "row" };
    case "grid": return { shape: "poster", display: "grid" };
    case "numbered": return { shape: "numbered", display: "row" };
    case "editorial": return { shape: "editorial", display: "row" };
    case "square-row": return { shape: "square", display: "row" };
    case "hero": return { shape: "editorial", display: "banner" };
    default: return { shape: "landscape", display: "row" };
  }
}

function badgesOf(item: FeedItem, now: number): PreviewBadge[] {
  const out: PreviewBadge[] = [];
  const starts = item.startsAt ? Date.parse(item.startsAt) : NaN;
  const ends = item.endsAt ? Date.parse(item.endsAt) : starts + 2 * 3_600_000;
  if (!Number.isNaN(starts) && starts <= now && now < ends) out.push({ label: "Live", tone: "live" });
  const released = item.releasedAt ? Date.parse(item.releasedAt) : NaN;
  if (!Number.isNaN(released) && now - released < 14 * DAY && released <= now) out.push({ label: "New", tone: "accent" });
  const access = item.entitlement?.access;
  if (access === "subscription") out.push({ label: "Members", tone: "neutral" });
  else if (access === "rental") out.push({ label: "Rent", tone: "neutral" });
  else if (access === "purchase") out.push({ label: "Buy", tone: "neutral" });
  return out.slice(0, 2);
}

function metaOf(item: FeedItem, kind: "title" | "series" | "live", locale: string): string | undefined {
  if (kind === "live" && item.startsAt) {
    const d = new Date(item.startsAt);
    return d.toLocaleString(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  }
  if (kind === "series") {
    const count = (item.seasons ?? []).reduce((n, s) => n + s.titleIds.length, 0);
    return count ? `${count} ${item.kind === "programme" ? (count === 1 ? "day" : "days") : count === 1 ? "part" : "parts"}` : undefined;
  }
  if (item.durationSeconds) {
    const m = Math.round(item.durationSeconds / 60);
    return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
  }
  return undefined;
}

export function cardOf(item: FeedItem, kind: "title" | "series" | "live", shape: CardShape, locale: string, now: number, extra: Partial<PreviewCard> = {}): PreviewCard {
  const img = imageFor(item.artwork, ROLES[shape].roles, ROLES[shape].width);
  return { id: item.id, title: text(item.title, locale) || item.id, meta: metaOf(item, kind, locale), image: img.url, color: img.color, badges: badgesOf(item, now), ...extra };
}

/** Every playable thing by id, with what it is. */
function index(feed: FeedLike) {
  const all = new Map<string, { item: FeedItem; kind: "title" | "series" | "live" }>();
  for (const t of feed.titles) all.set(t.id, { item: t, kind: "title" });
  for (const s of feed.series) all.set(s.id, { item: s, kind: "series" });
  for (const e of feed.liveEvents) all.set(e.id, { item: e, kind: "live" });
  return all;
}

const newestFirst = (a: FeedItem, b: FeedItem) => (Date.parse(b.releasedAt ?? "") || 0) - (Date.parse(a.releasedAt ?? "") || 0);

export function layoutScreen({ feed, rails, rowLabel, locale, now }: LayoutInput): PreviewScreen {
  const items = index(feed);
  const collection = (id?: string) => feed.collections.find((c) => c.id === id);
  const termName = (id: string) => {
    for (const t of feed.taxonomies ?? []) { const term = t.terms.find((x) => x.id === id); if (term) return text(term.title, locale); }
    return undefined;
  };
  const SAMPLE = "A sample: each viewer sees their own.";
  const rows: PreviewRow[] = [];

  for (const { rail, index: at } of rails) {
    const own = text(rail.title, locale);
    const typeLabel = rowLabel(rail.kind);
    const base = { index: at, title: own || typeLabel };
    const limit = rail.limit ?? 12;
    const resolve = (ids: string[], shape: CardShape) => ids.map((id) => items.get(id)).filter(Boolean).map((x, i) => cardOf(x!.item, x!.kind, shape, locale, now, shape === "numbered" ? { rank: i + 1 } : {}));

    switch (rail.kind) {
      case "collection": {
        const c = collection(rail.collectionId);
        if (!c) { rows.push({ ...base, title: own || "A collection", shape: "landscape", display: "row", cards: [], empty: rail.collectionId ? "Left out: this collection is not in the app yet (a draft, or not saved)." : "Choose a collection for this row." }); break; }
        const { shape, display } = shapeOf(c.layout);
        const cards = resolve(c.itemIds, shape);
        rows.push({ ...base, title: own || text(c.title, locale), shape, display, cards, ...(cards.length ? {} : { empty: "Nothing in this collection is in the app yet." }) });
        break;
      }
      case "collections": {
        const cards = (rail.collectionIds ?? []).map((id) => collection(id)).filter(Boolean).map((c) => {
          const first = c!.itemIds.map((id) => items.get(id)).find(Boolean);
          const own = imageFor(c!.artwork, ROLES.collection.roles, ROLES.collection.width);
          const img = own.url ? own : imageFor(first?.item.artwork, ROLES.collection.roles, ROLES.collection.width);
          return { id: c!.id, title: text(c!.title, locale), meta: `${c!.itemIds.length} ${c!.itemIds.length === 1 ? "item" : "items"}`, image: img.url, color: img.color, badges: [] } as PreviewCard;
        });
        rows.push({ ...base, title: own || "Collections", shape: "collection", display: "row", cards, ...(cards.length ? {} : { empty: "Choose the collections for this shelf." }) });
        break;
      }
      case "today": {
        const programme = feed.series.find((s) => s.kind === "programme");
        const ids = programme?.seasons?.flatMap((s) => s.titleIds) ?? [];
        const day = items.get(ids[Math.min(3, ids.length - 1)] ?? "");
        const cards = day ? [cardOf(day.item, day.kind, "editorial", locale, now, { meta: `Day ${Math.min(4, ids.length)} of ${ids.length} · ${text(programme!.title, locale)}` })] : [];
        rows.push({ ...base, title: own || "Today", shape: "editorial", display: "row", cards, sample: "A sample: each viewer sees the day they have reached.", ...(cards.length ? {} : { empty: "Shows nothing until a viewer follows a programme." }) });
        break;
      }
      case "continue-watching": {
        const cards = [...feed.titles].sort(newestFirst).slice(0, 6).map((t, i) => cardOf(t, "title", "landscape", locale, now, { progress: [0.35, 0.7, 0.2, 0.55, 0.8, 0.45][i] }));
        rows.push({ ...base, shape: "landscape", display: "row", cards, sample: SAMPLE });
        break;
      }
      case "my-list": {
        const cards = [...feed.titles, ...feed.series].slice(0, 8).map((t) => cardOf(t, feed.series.includes(t) ? "series" : "title", "poster", locale, now));
        rows.push({ ...base, shape: "poster", display: "row", cards, sample: SAMPLE });
        break;
      }
      case "up-next": {
        const cards = feed.series.map((s) => items.get(s.seasons?.[0]?.titleIds[0] ?? "")).filter(Boolean).slice(0, 8).map((x) => cardOf(x!.item, x!.kind, "landscape", locale, now));
        rows.push({ ...base, shape: "landscape", display: "row", cards, sample: SAMPLE });
        break;
      }
      case "my-series": {
        const cards = feed.series.slice(0, limit).map((s) => cardOf(s, "series", "poster", locale, now));
        rows.push({ ...base, shape: "poster", display: "row", cards, sample: SAMPLE });
        break;
      }
      case "recently-added": {
        const cards = [...feed.titles].sort(newestFirst).slice(0, limit).map((t) => cardOf(t, "title", "landscape", locale, now));
        rows.push({ ...base, shape: "landscape", display: "row", cards });
        break;
      }
      case "term": {
        const tagged = [...feed.titles, ...feed.series].filter((t) => rail.termId && t.terms?.includes(rail.termId)).slice(0, limit);
        const cards = tagged.map((t) => cardOf(t, feed.series.includes(t) ? "series" : "title", "landscape", locale, now));
        rows.push({ ...base, title: own || (rail.termId ? termName(rail.termId) : undefined) || base.title, shape: "landscape", display: "row", cards, ...(cards.length ? {} : { empty: rail.termId ? "Nothing in the app has this category yet." : "Choose what this row shows." }) });
        break;
      }
      case "live-now": {
        const live = feed.liveEvents.filter((e) => badgesOf(e, now).some((b) => b.tone === "live"));
        rows.push({ ...base, shape: "landscape", display: "row", cards: live.map((e) => cardOf(e, "live", "landscape", locale, now)), ...(live.length ? {} : { empty: "Nothing is live right now, so the apps hide this row." }) });
        break;
      }
      case "upcoming": {
        const soon = feed.liveEvents.filter((e) => Date.parse(e.startsAt ?? "") > now).sort((a, b) => Date.parse(a.startsAt!) - Date.parse(b.startsAt!));
        rows.push({ ...base, shape: "landscape", display: "row", cards: soon.slice(0, limit).map((e) => cardOf(e, "live", "landscape", locale, now)), ...(soon.length ? {} : { empty: "Nothing is coming up, so the apps hide this row." }) });
        break;
      }
      case "schedule": {
        const stages = feed.stages ?? [];
        const schedule = stages.map((s) => ({
          stage: text(s.title, locale),
          blocks: feed.liveEvents.filter((e) => e.stageId === s.id && e.startsAt).map((e) => {
            const start = Date.parse(e.startsAt!);
            return { title: text(e.title, locale), start, end: e.endsAt ? Date.parse(e.endsAt) : start + 3_600_000 };
          }).sort((a, b) => a.start - b.start),
        }));
        rows.push({ ...base, shape: "landscape", display: "schedule", cards: [], schedule, ...(schedule.some((s) => s.blocks.length) ? {} : { empty: "No sessions have a stage and a time yet." }) });
        break;
      }
      default:
        rows.push({ ...base, shape: "landscape", display: "row", cards: [], empty: "The apps draw this row; the preview does not know it yet." });
    }
  }

  // The hero: a banner row on top, else the first item of the first row that has one.
  const bannerFirst = rows[0]?.display === "banner" ? rows[0] : undefined;
  const source = bannerFirst ?? rows.find((r) => r.cards.length && !r.sample && r.shape !== "collection");
  let hero: PreviewHero | null = null;
  if (source?.cards.length) {
    const found = items.get(source.cards[0]!.id);
    if (found) {
      const img = imageFor(found.item.artwork, ["backdrop", "hero", "thumbnail", "poster"], 1920);
      const logo = imageFor(found.item.artwork, ["logo"], 600);
      hero = { title: text(found.item.title, locale), synopsis: text(found.item.synopsis, locale) || undefined, image: img.url, color: img.color, logo: logo.url, meta: metaOf(found.item, found.kind, locale), badges: badgesOf(found.item, now), fromRow: source.index };
    }
  }
  return { hero, rows: bannerFirst ? rows.slice(1) : rows };
}
